import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "../Common/Layout/Header";
import { useAuth } from "../Common/Configurations/AuthContext";
import apiService from "../Common/Configurations/APIService";
import config from "../Common/Configurations/APIConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGlobe,
  faUsers,
  faBriefcase,
  faBuilding,
  faShoppingCart,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import "./AgentWebsiteShowcase.css";
import WebsiteTabs from "../Components/AllTabs/WebsiteTabs";

const AgentWebsiteShowcase = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("Travel");
  const [dbTemplates, setDbTemplates] = useState([]);
  const [assignedTemplateId, setAssignedTemplateId] = useState(null);
  const [loading, setLoading] = useState(true);

  const categories = [
    { id: "Travel", label: "Travel Website", icon: faGlobe, color: "info" },
    { id: "B2C", label: "B2C Website", icon: faUsers, color: "success" },
    { id: "B2B", label: "B2B Website", icon: faBriefcase, color: "warning" },
    { id: "Corporate", label: "Corporate Website", icon: faBuilding, color: "primary" },
  ];

  const templateDatabase = {
    B2B: [
      {
        id: "static-b2b",
        title: "Wholesale Agent Portal",
        desc: "Inventory-heavy layout.",
        badge: "B2B Live",
        color: "primary",
        path: "https://b2b.trawtel.com/",
        img: "/assets/images/B2B_Website.png",
      },
    ],
    Corporate: [
      {
        id: "static-corp",
        title: "Corporate Command Center",
        desc: "Enterprise help desk.",
        badge: "Live Site",
        color: "success",
        path: "https://myfirstbooking.in",
        img: "/assets/images/Corporate_Website.png",
      },
    ],
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await apiService.getAll(config.GETALLTEMPLATES);
        const data = response.data?.items || response.data || response;

        const formatted = data.map((t) => ({
          id: t.templateId,
          title: t.name,
          desc: t.description || "Professional travel website template.",
          path: `/template-preview/${t.templateId}`,
          badge: t.templateType === "B2C Website" ? "B2C" : "Travel",
          color: t.templateType === "B2C Website" ? "success" : "info",
          img: t.thumbnailUrl || "https://via.placeholder.com/800x600?text=Template",
          type: t.templateType,
        }));

        setDbTemplates(formatted);

        if (user?.cid) {
          try {
            const assignedRes = await apiService.getAll(
              `${config.GETTEMPLATEBYCOMPANYID}/${user.cid}`
            );

            const tid =
              assignedRes?.data?.templateId ||
              assignedRes?.templateId ||
              assignedRes;

            if (tid) setAssignedTemplateId(tid);
          } catch {
            console.log("No assigned template");
          }
        }
      } catch (error) {
        console.error("Failed to fetch templates:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [user]);

  const currentTemplates = useMemo(() => {
    if (activeTab === "Travel")
      return dbTemplates.filter((t) => t.type === "Travel Website");

    if (activeTab === "B2C")
      return dbTemplates.filter((t) => t.type === "B2C Website");

    return templateDatabase[activeTab] || [];
  }, [activeTab, dbTemplates]);

  const activeTemplate = useMemo(
    () => dbTemplates.find((t) => t.id === assignedTemplateId),
    [dbTemplates, assignedTemplateId]
  );

  const renderTemplateCard = (demo) => {
    const isActive = demo.id === assignedTemplateId;

    return (
      <div className="agent-website-template-card shadow-lg">
        <div className="agent-website-img-container">
          <img
            src={demo.img}
            alt={demo.title}
            className="agent-website-img"
            onError={(e) =>
              (e.target.src =
                "https://via.placeholder.com/800x600?text=Preview")
            }
          />

          <div className="agent-website-img-overlay">
            <Link
              to={demo.path}
              target="_blank"
              className="btn btn-light rounded-pill px-3 fw-bold"
            >
              <FontAwesomeIcon icon={faEye} className="me-1" />
              View Live
            </Link>
          </div>

          {isActive && (
            <span className="badge bg-success agent-website-template-badge">
              Active Website
            </span>
          )}
        </div>

        <div className="card-body p-4 text-center">
          <h5 className="fw-bold text-truncate">{demo.title}</h5>

          <p
            className="text-muted small mb-4"
            style={{ height: "40px", overflow: "hidden" }}
          >
            {demo.desc}
          </p>

          <div className="agent-website-btn-group">
            <Link
              to={demo.path}
              target="_blank"
              className="agent-website-btn-preview"
            >
              Preview
            </Link>

            {isActive ? (
              <button className="btn btn-success w-100" disabled>
                ✓ Selected
              </button>
            ) : (
              <button
                className="agent-website-btn-cart"
                onClick={() => alert(`${demo.title} added to cart`)}
              >
                <FontAwesomeIcon icon={faShoppingCart} className="me-1" />
                Add to Cart
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      id="layout-wrapper"
      className="agent-website-main-wrapper"
      style={{ background: "#f3f4f6", minHeight: "100vh" }}
    >
      <Header User={user} activeSection="website" />
<div className="main-content">
  <div className="page-content">
    <div className="container-fluid">

      <div className="card border-0 shadow-sm">
        <div className="p-2">
          <WebsiteTabs pageName="AGENTWEBSITESHOWCASE" />
        </div>

        <div className="card-body"></div>
          {/* ACTIVE TEMPLATE AT TOP */}
          {activeTab === "Travel" && activeTemplate && (
            <div className="mb-1 bg-light p-4 rounded border text-center">
              <h3 className="fw-bold text-primary mb-1">
                ⭐ Your Active Website
              </h3>

              <div className="row justify-content-center">
                <div className="col-xl-3 col-md-6 mb-1">
                  {renderTemplateCard(activeTemplate)}
                </div>
              </div>
            </div>
          )}

          {/* WEBSITE SHOWCASE TITLE */}
          <div className="row mb-4 text-center">
            <div className="col-lg-8 mx-auto">
              <h2 className="agent-website-page-title">
                Website Showcase
              </h2>

              <p className="text-muted">
                Explore and select high-converting templates for your
                travel business.
              </p>
            </div>
          </div>

          {/* CATEGORY CARDS */}
          <div className="row justify-content-center mb-5">
            {categories.map((cat) => (
              <div key={cat.id} className="col-xl-3 col-md-6 mb-3">
                <div
                  className={`agent-website-category-card ${
                    activeTab === cat.id
                      ? "agent-website-active-category"
                      : ""
                  }`}
                  onClick={() => setActiveTab(cat.id)}
                >
                  <div className="card-body text-center p-4">
                    <div
                      className={`agent-website-icon-wrapper agent-website-bg-soft-${cat.color} mb-3`}
                    >
                      <FontAwesomeIcon
                        icon={cat.icon}
                        className={`text-${cat.color} fs-3`}
                      />
                    </div>

                    <h5 className="fw-bold text-dark mb-1">
                      {cat.label}
                    </h5>

                    <span className="text-muted small">
                      Templates
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* MORE TEMPLATES */}
          <div>
            <h4 className="fw-bold text-secondary mb-4">
              {activeTab === "Travel"
                ? "Explore More Travel Websites"
                : "Available Templates"}
            </h4>

            <div className="row justify-content-center">
              {loading ? (
                <div className="spinner-border text-primary"></div>
              ) : currentTemplates.length > 0 ? (
                currentTemplates
                  .filter((t) => t.id !== assignedTemplateId)
                  .map((demo) => (
                    <div
                      key={demo.id}
                      className="col-xl-3 col-md-6 mb-4"
                    >
                      {renderTemplateCard(demo)}
                    </div>
                  ))
              ) : (
                <div className="text-muted text-center py-5">
                  No templates found
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
    </div>
    </div>
  );
};

export default AgentWebsiteShowcase;